import { Avatar, Button, EmptyState, emptyStates, formatDuration, formatPrice, formatTime } from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { SALON_ID, SALON_TIMEZONE as TZ, api } from "../lib/trpc";
import { todayKey, zonedEndOfDay, zonedStartOfDay } from "../lib/time";

/**
 * L'écran de 8h50, café en main.
 *
 * Il répond à QUATRE questions et rien d'autre : qu'est-ce qui n'est pas
 * validé, qui n'a pas répondu, où sont les trous, combien on fait aujourd'hui.
 *
 * Parti pris : une manager n'a pas besoin de statistiques, elle a besoin
 * d'EXCEPTIONS. Tout ce qui va bien reste discret ; ce qui cloche prend la
 * place. C'est pourquoi la file « à confirmer » occupe la moitié de l'écran
 * même quand elle contient deux lignes — et disparaît en état vide heureux
 * quand elle est vide.
 */
export function Pulse({ onOpenInbox }: { onOpenInbox: () => void }) {
  const queryClient = useQueryClient();
  const dateKey = todayKey(TZ);
  const from = useMemo(() => zonedStartOfDay(dateKey, TZ), [dateKey]);
  const to = useMemo(() => zonedEndOfDay(dateKey, TZ), [dateKey]);

  const pulse = useQuery({
    queryKey: ["pulse", dateKey],
    queryFn: () => api.agenda.managerPulse.query({ salonId: SALON_ID, from, to }),
    // La file « à confirmer » se vide vite : on la garde fraîche.
    refetchInterval: 60_000,
  });

  const decide = useMutation({
    mutationFn: (input: { appointmentId: string; accept: boolean }) =>
      input.accept
        ? api.agenda.confirm.mutate({ appointmentId: input.appointmentId })
        : api.agenda.decline.mutate({ appointmentId: input.appointmentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pulse"] });
      queryClient.invalidateQueries({ queryKey: ["planning"] });
    },
  });

  const data = pulse.data;
  const occupancy = data ? Math.round(data.today.minutesBooked / 60) : 0;

  return (
    <div className="h-full overflow-auto px-6 py-4">
      <h1 className="mb-1 text-2xl">Aujourd&apos;hui</h1>
      <p className="mb-6 text-encre-500">
        {new Intl.DateTimeFormat("fr-FR", {
          timeZone: TZ,
          weekday: "long",
          day: "numeric",
          month: "long",
        }).format(from)}
      </p>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Stat label="Rendez-vous" value={String(data?.today.booked ?? "—")} hint={`${occupancy} h réservées`} />
        <Stat
          label="Chiffre prévu"
          value={data ? formatPrice(data.today.revenueCents) : "—"}
          hint={data ? `${formatPrice(data.today.completedCents)} encaissés` : undefined}
        />
        <button type="button" onClick={onOpenInbox} className="text-left">
          <Stat
            label="Messages en attente"
            value={String(data?.unreadMessages ?? "—")}
            hint={data ? `${data.unreadConversations} conversation(s)` : undefined}
            alert={(data?.unreadMessages ?? 0) > 0}
          />
        </button>
      </div>

      <h2 className="mb-3 text-xl">À confirmer</h2>

      {pulse.isPending && <p className="text-encre-400">Chargement…</p>}

      {data && data.pendingAppointments.length === 0 && (
        <EmptyState variant="card" {...emptyStates.noPending} />
      )}

      <ul className="space-y-2">
        {data?.pendingAppointments.map((appointment) => (
          <li
            key={appointment.id}
            className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-soft animate-fade-up"
          >
            <Avatar
              src={appointment.client?.avatarUrl}
              firstName={appointment.client?.firstName ?? appointment.guestName}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg leading-tight">
                {appointment.client?.firstName ?? appointment.guestName ?? "Cliente"}
              </p>
              <p className="truncate text-sm text-encre-500">
                {new Intl.DateTimeFormat("fr-FR", {
                  timeZone: TZ,
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                }).format(appointment.startsAt)}{" "}
                à {formatTime(appointment.startsAt, TZ)} ·{" "}
                {appointment.lines.map((line) => line.nameSnapshot).join(" + ")} ·{" "}
                {formatDuration(appointment.totalDurationMinutes)} avec {appointment.staff.firstName}
              </p>
              {appointment.clientNote && (
                <p className="mt-1 truncate text-sm italic text-encre-400">
                  « {appointment.clientNote} »
                </p>
              )}
            </div>

            {/* Deux boutons, jamais un menu : c'est une décision binaire. */}
            <div className="flex shrink-0 gap-2">
              <Button
                variant="ghost"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ appointmentId: appointment.id, accept: false })}
              >
                Refuser
              </Button>
              <Button
                disabled={decide.isPending}
                onClick={() => decide.mutate({ appointmentId: appointment.id, accept: true })}
              >
                Confirmer
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  alert,
}: {
  label: string;
  value: string;
  hint?: string;
  alert?: boolean;
}) {
  return (
    <div
      className={[
        "rounded-xl bg-white p-4 shadow-soft transition-colors",
        alert ? "ring-2 ring-blush-300" : "",
      ].join(" ")}
    >
      <p className="text-sm text-encre-500">{label}</p>
      <p className="font-display text-3xl leading-tight text-encre-900" data-numeric>
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-encre-400">{hint}</p>}
    </div>
  );
}
